import React from "react"
import { useTranslation } from "react-i18next"
import { RoutingProfile } from "@repo/core/models"
import { compact } from "es-toolkit"

import { Badge } from "@/components/ui/badge"
import { Combobox } from "@/components/ui/combobox"
import { GenericCombobox } from "@/components/ui/generic-combobox"

type Props = {
  onChange: (value: string) => void
  routingProfiles: RoutingProfile[]
  value: string
}

export function RoutingProfilePicker({
  onChange,
  ref,
  routingProfiles,
  value,
}: { ref?: React.Ref<React.ElementRef<typeof Combobox>> } & Props) {
  return (
    <GenericCombobox<RoutingProfile>
      ref={ref}
      value={value}
      customFilterFunction={customRoutingProfileFilter}
      options={routingProfiles}
      getOptionValue={(option) => option.id}
      renderOption={(option) => <ShowRoutingProfile option={option} />}
      renderButton={(option) => (
        <div>
          {option.name} <NumberOfUserBadge option={option} />
        </div>
      )}
      getOptionKeywords={(option) => compact([option.name, option.description])}
      onChange={onChange}
      size="3xl"
    />
  )
}

function ShowRoutingProfile({ option }: { option: RoutingProfile }) {
  return (
    <div className="flex flex-col items-start">
      <div className="font-medium">
        {option.name}
        <NumberOfUserBadge option={option} />
      </div>
      {option.description && (
        <div className="text-xs text-muted-foreground">
          {option.description}
        </div>
      )}
    </div>
  )
}

function NumberOfUserBadge({ option }: { option: RoutingProfile }) {
  const { t } = useTranslation()
  if (!option.number_of_users) return null

  return (
    <Badge variant="outline" className="ml-1">
      {t("global.peopleCount", { count: option.number_of_users })}
    </Badge>
  )
}

/**
 * Override the default filter function provided by CmdK package, that is not strict enough
 * Default implementation: https://github.com/pacocoursey/cmdk/blob/main/cmdk/src/command-score.ts
 * */
function customRoutingProfileFilter(
  value: string,
  search: string,
  keywords: string[] | undefined,
) {
  if (value.toLowerCase().includes(search.toLowerCase())) {
    return 1
  }

  // Less relevant matches
  if (
    keywords &&
    keywords.some((keyword) =>
      keyword.toLowerCase().includes(search.toLowerCase()),
    )
  ) {
    return 0.5
  }

  return 0
}
